import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserAuth } from '../context/AuthContext';
import { db } from "../firebase";
import { collection, onSnapshot, query, where, doc, setDoc } from "firebase/firestore";
import { motion, AnimatePresence } from 'framer-motion';
import { addJuceListener, removeJuceListener } from './juceInterop';
import SetupPluginManager from './SetupPluginManager';
import './setup.css';

const StepIcon = ({ active, completed, number }) => (
    <motion.div 
        layout
        style={{
            width: '28px', height: '28px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: completed ? 'rgba(51, 230, 102, 0.2)' : active ? 'rgba(100, 150, 255, 0.2)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${completed ? 'rgba(51, 230, 102, 0.5)' : active ? 'rgba(100, 150, 255, 0.5)' : 'rgba(255,255,255,0.1)'}`,
            color: completed ? '#33e666' : active ? '#6496ff' : '#7b8599',
            fontSize: '13px', fontWeight: 'bold', 
            boxShadow: active ? '0 0 16px rgba(100, 150, 255, 0.2)' : 'none'
        }}
    >
        {completed ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg> : number}
    </motion.div>
);

const SetupLicenses = () => {
    const [allOwnedProducts, setAllOwnedProducts] = useState([]);
    const [viewState, setViewState] = useState('IDLE'); // IDLE, ACTIVATING, DOWNLOADING, SCANNING, COMPLETE
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [scanStatus, setScanStatus] = useState({ current: 0, total: 0, pluginName: '' });
    const [error, setError] = useState('');
    const [productSignatures, setProductSignatures] = useState({});
    const [systemPlugins, setSystemPlugins] = useState([]);

    const { user, logOut } = UserAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search || window.location.search);
    const urlMachineId = searchParams.get('machine_id') || localStorage.getItem('setup_machine_id');

    const tips = [
        "Constructing plugin neural pathways...",
        "Indexing high-fidelity VST3 & AU pipelines.",
        "Establishing zero-latency sandbox bypass.",
        "Calibrating Tape Signal DSP infrastructure...",
        "Securing offline hardware entitlement..."
    ];

    useEffect(() => {
        if (user && user.uid) {
            const q = query(collection(db, "purchasedProducts"), where("user", "==", user.uid));
            onSnapshot(q, (snapshot) => {
                setAllOwnedProducts(snapshot.docs.map(document => {
                    const productData = document.data();
                    const availableLicenses = [productData.license1, productData.license2].filter(
                        (license) => license !== ""
                    ).length;

                    return {
                        id: document.id,
                        availableLicenses,
                        ...productData
                    };
                }));
            });
        }
    }, [user]);

    useEffect(() => {
        if (viewState === 'SCANNING') {
            const interval = setInterval(() => {
                setCurrentTipIndex(prev => (prev + 1) % tips.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [viewState]);

    const capitalizeFirstLetter = (string) => string ? string.charAt(0).toUpperCase() + string.slice(1) : '';

    const fetchSystemPlugins = async () => {
        if (window.__JUCE__?.backend?.getPluginList) {
            const list = await window.__JUCE__.backend.getPluginList();
            setSystemPlugins(list || []);
        }
    };

    useEffect(() => {
        fetchSystemPlugins();
        
        const scanProgressId = addJuceListener('scanProgress', (data) => {
            setScanStatus(data);
            if (data?.plugins && Array.isArray(data.plugins)) {
                setSystemPlugins(data.plugins);
            }
        });
        const scanFinishedId = addJuceListener('scanFinished', () => {
            fetchSystemPlugins();
            setViewState((currentViewState) => {
                if (currentViewState === 'SCANNING') return 'COMPLETE';
                return currentViewState;
            });
        });
        return () => {
            removeJuceListener(scanProgressId);
            removeJuceListener(scanFinishedId);
        };
    }, []);

    useEffect(() => {
        const fetchSigs = async () => {
            if (!urlMachineId || !user?.email || allOwnedProducts.length === 0) return;
            const sigs = {};
            for (const product of allOwnedProducts) {
                const cleanProductName = product.name.toLowerCase() === 'refract' ? 'Refract' : capitalizeFirstLetter(product.name);
                const functionURL = `https://us-central1-ts-web-5390d.cloudfunctions.net/generateLicense`;
                try {
                    const response = await fetch(`${functionURL}?machine_id=${urlMachineId}&product=${cleanProductName}&user_email=${user.email}&json=true`);
                    if (response.ok) {
                        const { signature } = await response.json();
                        sigs[product.id] = signature;
                    }
                } catch (e) {}
            }
            setProductSignatures(sigs);
        };
        fetchSigs();
    }, [allOwnedProducts, urlMachineId, user]);

    const handleSetupAll = async () => {
        if (!urlMachineId) {
            setError("Native Machine ID missing. Please launch via Tape Signal Setup App.");
            return;
        }

        setViewState('ACTIVATING');
        setError('');

        const triggerNative = (url) => {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            setTimeout(() => document.body.removeChild(iframe), 2000);
        };

        try {
            let anyActivated = false;
            let productsToDownload = [];

            for (const product of allOwnedProducts) {
                const cleanProductName = product.name.toLowerCase() === 'refract' ? 'Refract' : capitalizeFirstLetter(product.name);
                let sig = productSignatures[product.id];
                
                if (!sig) {
                    const functionURL = `https://us-central1-ts-web-5390d.cloudfunctions.net/generateLicense`;
                    const response = await fetch(`${functionURL}?machine_id=${urlMachineId}&product=${cleanProductName}&user_email=${user.email}&json=true`);
                    if (response.ok) {
                        const resJson = await response.json();
                        sig = resJson.signature;
                    }
                }
                if (!sig) continue;

                const isAlreadyOnThisMachine = (product.license1 === sig || product.license2 === sig);
                const isInstalledLocally = systemPlugins.some(p => p.name.toLowerCase().includes(cleanProductName.toLowerCase()));

                if (isAlreadyOnThisMachine) {
                    triggerNative(`tapesignal://setup/${cleanProductName.toLowerCase()}/lic/2/${sig}/${user.email}`);
                    if (!isInstalledLocally) {
                        productsToDownload.push(cleanProductName.toLowerCase());
                        anyActivated = true;
                    }
                } else if (product.availableLicenses < 2) {
                    let payload = product.license1 === "" ? { license1: sig } : { license2: sig };
                    await setDoc(doc(db, "purchasedProducts", product.id), payload, { merge: true });
                    triggerNative(`tapesignal://setup/${cleanProductName.toLowerCase()}/lic/2/${sig}/${user.email}`);
                    if (!isInstalledLocally) {
                        productsToDownload.push(cleanProductName.toLowerCase());
                        anyActivated = true;
                    }
                }
            }

            if (productsToDownload.length > 0 || anyActivated) {
                setViewState('DOWNLOADING');
                
                let delay = 1000;
                for (const prodName of productsToDownload) {
                    setTimeout(() => {
                        const downloadUrl = `https://us-central1-ts-web-5390d.cloudfunctions.net/downloadProduct?product=${prodName}`;
                        triggerNative(`tapesignal://setup-app-action/install/${prodName}?url=${encodeURIComponent(downloadUrl)}`);
                    }, delay);
                    delay += 12000; 
                }
                
                setTimeout(() => setViewState('SCANNING'), delay + 1000);
            } else {
                setViewState('COMPLETE');
            }

        } catch (err) {
            console.error(err);
            setError("Setup interrupted: " + err.message);
            setViewState('IDLE');
        }
    };

    const handleTestReset = async () => {
        setViewState('IDLE');
        if (window.__JUCE__?.backend?.clearPlugins) {
            await window.__JUCE__.backend.clearPlugins();
        }
        setError("Test Environment Reset! C++ Scanner DB wiped.");
        setTimeout(() => setError(""), 3000);
    };

    const isFullyFilled = allOwnedProducts.length > 0 && allOwnedProducts.every(p => p.availableLicenses === 2);

    return (
        <div className="setup-app-container">
            <div style={{ position: 'absolute', top: 30, right: 30, display: 'flex', gap: '16px', alignItems: 'center', zIndex: 10 }}>
                <button onClick={handleTestReset} style={{ background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.3)', color: '#ff4444', cursor: 'pointer', fontSize: '11px', padding: '6px 12px', borderRadius: '8px', transition: '0.2s' }}>
                    [TEST] Reset DB
                </button>
                <button onClick={() => logOut().then(() => navigate('/setup/signin'))} style={{ background: 'transparent', border: 'none', color: '#7b8599', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                    Sign Out ({user?.email})
                </button>
            </div>

            <motion.div
                className="setup-glass-panel"
                initial={{ opacity: 0, scale: 0.9, y: 30, rotateX: 10 }} 
                animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.8, type: "spring", bounce: 0.3 }}
                style={{ perspective: 1000, margin: '40px auto' }}
            >
                <motion.h1 
                    className="setup-header"
                    initial={{ opacity: 0, filter: 'blur(10px)' }} animate={{ opacity: 1, filter: 'blur(0px)' }} transition={{ delay: 0.2, duration: 0.8 }}
                >
                    {viewState === 'IDLE' ? "Ecosystem Logic." : viewState === 'COMPLETE' ? "System Ready." : "Initialization..."}
                </motion.h1>

                <motion.p 
                    className="setup-subtitle"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                >
                    {viewState === 'IDLE' && "Initialize your hardware to securely bind licenses and bridge your DAW environment."}
                    {viewState === 'ACTIVATING' && "Generating cryptographically secure RSA machine signatures..."}
                    {viewState === 'DOWNLOADING' && "Acquiring Tape Signal core platform binaries..."}
                    {viewState === 'SCANNING' && "Building cross-application plugin indexing matrix..."}
                    {viewState === 'COMPLETE' && "Architecture successfully deployed. You may close this window."}
                </motion.p>

                {error && <div className="setup-error">{error}</div>}

                {viewState === 'IDLE' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 0 20px 0' }}>
                        {allOwnedProducts.map(product => {
                            const cleanName = product.name.toLowerCase() === 'refract' ? 'Refract' : capitalizeFirstLetter(product.name);
                            const sig = productSignatures[product.id];
                            
                            const isBoundToThisPc = sig && (product.license1 === sig || product.license2 === sig);
                            const isInstalledLocally = systemPlugins.some(p => p.name.toLowerCase().includes(cleanName.toLowerCase()));
                            
                            let statusText = "Ready to Install";
                            let statusColor = "#6496ff"; 
                            
                            if (isBoundToThisPc && isInstalledLocally) {
                                statusText = "Installed & Active";
                                statusColor = "#33e666"; 
                            } else if (isBoundToThisPc && !isInstalledLocally) {
                                statusText = "Ready to Re-Install";
                                statusColor = "#e6a233"; 
                            } else if (product.availableLicenses >= 2 && !isBoundToThisPc) {
                                statusText = "No Licenses Available";
                                statusColor = "#ff4444"; 
                            }

                            return (
                                <div key={product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.03)', padding: '14px 18px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div style={{ color: '#fff', fontSize: '15px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: statusColor, boxShadow: `0 0 10px ${statusColor}` }}></div>
                                        {cleanName}
                                    </div>
                                    <div style={{ fontSize: '11px', fontWeight: '600', color: statusColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {statusText}
                                    </div>
                                </div>
                            );
                        })}

                        {allOwnedProducts.length > 0 && (
                            <button className="setup-btn-primary" onClick={handleSetupAll} style={{ marginTop: '20px' }}>
                                Set Up All
                            </button>
                        )}
                    </motion.div>
                )}

                <AnimatePresence mode="popLayout">
                    {(viewState !== 'IDLE' && viewState !== 'COMPLETE') && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                            style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                        >
                            <div className={`setup-step-item ${viewState === 'ACTIVATING' ? 'active' : 'completed'}`}>
                                <StepIcon active={viewState === 'ACTIVATING'} completed={viewState !== 'ACTIVATING'} number="1" />
                                <div className="setup-step-text-wrap">
                                    <span className="setup-step-title">Hardware Authentication</span>
                                    {viewState === 'ACTIVATING' && <span className="setup-step-desc">Signing local machine enclave.</span>}
                                </div>
                            </div>

                            <div className={`setup-step-item ${viewState === 'DOWNLOADING' ? 'active' : (viewState === 'SCANNING' ? 'completed' : '')}`} style={{ opacity: viewState === 'ACTIVATING' ? 0.4 : 1 }}>
                                <StepIcon active={viewState === 'DOWNLOADING'} completed={viewState === 'SCANNING'} number="2" />
                                <div className="setup-step-text-wrap">
                                    <span className="setup-step-title">Deploy Core Platform</span>
                                    {viewState === 'DOWNLOADING' && <span className="setup-step-desc">Fetching encrypted payload.</span>}
                                </div>
                            </div>

                            <div className={`setup-step-item ${viewState === 'SCANNING' ? 'active' : ''}`} style={{ opacity: viewState === 'SCANNING' ? 1 : 0.4 }}>
                                <div style={{ position: 'relative' }}>
                                    <StepIcon active={viewState === 'SCANNING'} completed={false} number="3" />
                                    {viewState === 'SCANNING' && (
                                        <svg className="spin-anim" style={{ position: 'absolute', top: -2, left: -2, pointerEvents: 'none', opacity: 0.8 }} width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6496ff" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="40 20">
                                            <circle cx="12" cy="12" r="11" />
                                        </svg>
                                    )}
                                </div>
                                <div className="setup-step-text-wrap">
                                    <span className="setup-step-title" style={{ color: viewState === 'SCANNING' ? '#fff' : '' }}>Plugin Matrix Construction</span>
                                    {viewState === 'SCANNING' && (
                                        <div style={{ height: '20px', position: 'relative', overflow: 'hidden', marginTop: '4px' }}>
                                            <AnimatePresence mode="wait">
                                                <motion.span
                                                    key={currentTipIndex}
                                                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                                                    className="setup-step-desc"
                                                    style={{ position: 'absolute', width: '100%' }}
                                                >
                                                    {tips[currentTipIndex]}
                                                </motion.span>
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {viewState === 'COMPLETE' && (
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring' }}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', padding: '20px 0' }}
                        >
                            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(51, 230, 102, 0.15)', color: '#33e666', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(51, 230, 102, 0.3)', boxShadow: '0 0 30px rgba(51, 230, 102, 0.2)' }}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            </div>
                            <button className="setup-btn-primary" onClick={() => window.close()} style={{ width: 'auto', padding: '16px 40px' }}>Close Window</button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <AnimatePresence>
                <motion.div
                    className="setup-licenses-wrapper"
                    initial={{ opacity: 0, y: 40, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.6, delay: 0.2, type: 'spring', bounce: 0.2 }}
                >
                    <SetupPluginManager allOwnedProducts={allOwnedProducts} />
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default SetupLicenses;
